import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Trash2,
  ArrowRight,
} from "lucide-react";

const RemoveNthNodeVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Remove 2nd from end",
      list: [1, 2, 3, 4, 5],
      n: 2,
      expected: [1, 2, 3, 5],
    },
    {
      id: 2,
      name: "Single node",
      list: [1],
      n: 1,
      expected: [],
    },
    {
      id: 3,
      name: "Remove last node",
      list: [1, 2],
      n: 1,
      expected: [1],
    },
    {
      id: 4,
      name: "Remove first node",
      list: [1, 2, 3],
      n: 3,
      expected: [2, 3],
    },
    {
      id: 5,
      name: "Remove from middle",
      list: [1, 2, 3, 4, 5],
      n: 3,
      expected: [1, 2, 4, 5],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [customN, setCustomN] = useState("");
  const [list, setList] = useState(testCases[0].list);
  const [n, setN] = useState(testCases[0].n);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Algorithm approach
  const [approach, setApproach] = useState("twoPointer"); // "twoPointer" or "twoPass"

  // Pseudo code for both approaches
  const twoPointerPseudoCode = [
    { line: "function removeNthFromEnd(head, n):", indent: 0 },
    { line: "dummy = new ListNode(0)", indent: 1 },
    { line: "dummy.next = head", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Initialize two pointers", indent: 1 },
    { line: "slow = dummy", indent: 1 },
    { line: "fast = dummy", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Move fast n+1 steps ahead", indent: 1 },
    { line: "for i = 0 to n:", indent: 1 },
    { line: "fast = fast.next", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Move both pointers together", indent: 1 },
    { line: "while fast != null:", indent: 1 },
    { line: "slow = slow.next", indent: 2 },
    { line: "fast = fast.next", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Remove the node", indent: 1 },
    { line: "slow.next = slow.next.next", indent: 1 },
    { line: "return dummy.next", indent: 1 },
  ];

  const twoPassPseudoCode = [
    { line: "function removeNthFromEnd(head, n):", indent: 0 },
    { line: "// First pass: count nodes", indent: 1 },
    { line: "length = 0", indent: 1 },
    { line: "current = head", indent: 1 },
    { line: "while current != null:", indent: 1 },
    { line: "length++", indent: 2 },
    { line: "current = current.next", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Calculate position from start", indent: 1 },
    { line: "position = length - n", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Second pass: remove node", indent: 1 },
    { line: "if position == 0:", indent: 1 },
    { line: "return head.next", indent: 2 },
    { line: "", indent: 0 },
    { line: "current = head", indent: 1 },
    { line: "for i = 0 to position - 1:", indent: 1 },
    { line: "current = current.next", indent: 2 },
    { line: "current.next = current.next.next", indent: 1 },
    { line: "return head", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate steps for two-pointer approach
  const calculateTwoPointerSteps = (arr, targetN) => {
    const allSteps = [];
    const nodes = arr.map((val, idx) => ({ value: val, index: idx }));

    // Step 0: Initial state with dummy
    allSteps.push({
      step: 0,
      nodes: [{ value: 0, index: -1, isDummy: true }, ...nodes],
      slowIndex: -1,
      fastIndex: -1,
      description: "Initialize dummy node and pointers",
      highlightLine: 1,
      phase: "init",
    });

    // Move fast pointer n+1 steps
    let fastIdx = -1;
    for (let i = 0; i <= targetN; i++) {
      fastIdx++;
      allSteps.push({
        step: allSteps.length,
        nodes: [{ value: 0, index: -1, isDummy: true }, ...nodes],
        slowIndex: -1,
        fastIndex: fastIdx,
        description: `Move fast pointer: step ${i + 1} of ${targetN + 1}`,
        highlightLine: 10,
        phase: "setup-fast",
        distance: i,
      });
    }

    // Move both pointers
    let slowIdx = -1;
    while (fastIdx < nodes.length) {
      slowIdx++;
      fastIdx++;

      allSteps.push({
        step: allSteps.length,
        nodes: [{ value: 0, index: -1, isDummy: true }, ...nodes],
        slowIndex: slowIdx,
        fastIndex: fastIdx,
        description: `Move both pointers forward`,
        highlightLine: 14,
        phase: "move-both",
        distance: targetN,
      });
    }

    // Identify node to remove
    const removeIdx = slowIdx + 1;
    allSteps.push({
      step: allSteps.length,
      nodes: [{ value: 0, index: -1, isDummy: true }, ...nodes],
      slowIndex: slowIdx,
      fastIndex: fastIdx,
      removeIndex: removeIdx,
      description: `Slow.next is the node to remove (value: ${
        nodes[removeIdx - 1]?.value
      })`,
      highlightLine: 17,
      phase: "identify",
    });

    // Remove the node
    const newNodes = nodes.filter((_, idx) => idx !== removeIdx - 1);
    allSteps.push({
      step: allSteps.length,
      nodes: newNodes,
      slowIndex: -1,
      fastIndex: -1,
      description: `Node removed! New list: [${newNodes
        .map((n) => n.value)
        .join(", ")}]`,
      highlightLine: 18,
      phase: "complete",
      isFinal: true,
    });

    return allSteps;
  };

  // Calculate steps for two-pass approach
  const calculateTwoPassSteps = (arr, targetN) => {
    const allSteps = [];
    const nodes = arr.map((val, idx) => ({ value: val, index: idx }));

    // First pass: count nodes
    let length = 0;
    for (let i = 0; i <= arr.length; i++) {
      allSteps.push({
        step: allSteps.length,
        nodes: nodes,
        currentIndex: i < arr.length ? i : -1,
        length: i,
        description:
          i < arr.length
            ? `Counting nodes: ${i + 1}`
            : `Total length: ${length}`,
        highlightLine: i < arr.length ? 5 : 6,
        phase: "count",
      });
      length = i;
    }

    // Calculate position
    const position = length - targetN;
    allSteps.push({
      step: allSteps.length,
      nodes: nodes,
      currentIndex: -1,
      targetPosition: position,
      description: `Position from start: ${length} - ${targetN} = ${position}`,
      highlightLine: 9,
      phase: "calculate",
    });

    // Second pass: find and remove
    if (position === 0) {
      // Remove first node
      allSteps.push({
        step: allSteps.length,
        nodes: nodes.slice(1),
        description: `Remove first node. New list: [${nodes
          .slice(1)
          .map((n) => n.value)
          .join(", ")}]`,
        highlightLine: 12,
        phase: "complete",
        isFinal: true,
      });
    } else {
      // Navigate to position
      for (let i = 0; i < position; i++) {
        allSteps.push({
          step: allSteps.length,
          nodes: nodes,
          currentIndex: i,
          targetPosition: position - 1,
          removeIndex: position,
          description: `Navigate to position ${i + 1}`,
          highlightLine: 16,
          phase: "navigate",
        });
      }

      // Remove node
      const newNodes = nodes.filter((_, idx) => idx !== position);
      allSteps.push({
        step: allSteps.length,
        nodes: newNodes,
        description: `Node removed! New list: [${newNodes
          .map((n) => n.value)
          .join(", ")}]`,
        highlightLine: 17,
        phase: "complete",
        isFinal: true,
      });
    }

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps =
      approach === "twoPointer"
        ? calculateTwoPointerSteps(list, n)
        : calculateTwoPassSteps(list, n);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [list, n, approach]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setList(testCases[index].list);
    setN(testCases[index].n);
  };

  const addCustomTestCase = () => {
    try {
      const cleanInput = customInput.trim().replace(/[\[\]]/g, "");
      const numbers = cleanInput.split(",").map((n) => parseInt(n.trim()));
      const nValue = parseInt(customN);

      if (numbers.some(isNaN) || numbers.length === 0) {
        alert("Please enter valid numbers");
        return;
      }

      if (isNaN(nValue) || nValue < 1 || nValue > numbers.length) {
        alert(`N must be between 1 and ${numbers.length}`);
        return;
      }

      // Calculate expected result
      const expected = [...numbers];
      expected.splice(numbers.length - nValue, 1);

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 4}`,
        list: numbers,
        n: nValue,
        expected: expected,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
      setCustomN("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Trash2 className="text-blue-500" size={24} />
        Remove Nth Node From End
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Approach Selector */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Select Approach</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setApproach("twoPointer");
                  reset();
                }}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "twoPointer"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Two Pointer - O(n)
              </button>
              <button
                onClick={() => {
                  setApproach("twoPass");
                  reset();
                }}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "twoPass"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Two Pass - O(n)
              </button>
            </div>
          </div>

          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      List: [{tc.list.join(", ")}], n = {tc.n}
                    </div>
                    <div className="text-xs opacity-75">
                      Expected: [{tc.expected.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <div className="flex gap-1">
                <input
                  type="text"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  placeholder="List: 1,2,3,4,5"
                  className="flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="number"
                  value={customN}
                  onChange={(e) => setCustomN(e.target.value)}
                  placeholder="n: 2"
                  min="1"
                  className="w-16 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Linked List Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Info Display */}
            {approach === "twoPointer" &&
              currentStepData?.distance !== undefined && (
                <div className="mb-3 p-2 bg-blue-50 rounded">
                  <p className="text-xs text-blue-800">
                    Distance between pointers: {currentStepData.distance} nodes
                  </p>
                </div>
              )}

            {approach === "twoPass" &&
              currentStepData?.length !== undefined && (
                <div className="mb-3 p-2 bg-blue-50 rounded">
                  <p className="text-xs text-blue-800">
                    {currentStepData.phase === "count"
                      ? `Counted nodes: ${currentStepData.length}`
                      : `Total length: ${currentStepData.length}`}
                  </p>
                </div>
              )}

            {/* Linked List Visual */}
            <div className="overflow-x-auto py-4">
              <div className="flex items-center gap-2">
                {currentStepData?.nodes?.map((node, idx) => (
                  <div key={idx} className="relative">
                    <div
                      className={`w-12 h-12 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        node.isDummy
                          ? "bg-gray-300 border-gray-500"
                          : idx - 1 === currentStepData.removeIndex
                          ? "bg-red-200 border-red-500 scale-110 animate-pulse"
                          : idx === currentStepData.slowIndex + 1 &&
                            approach === "twoPointer"
                          ? "bg-orange-200 border-orange-500"
                          : idx === currentStepData.fastIndex + 1 &&
                            approach === "twoPointer"
                          ? "bg-green-200 border-green-500"
                          : idx === currentStepData.currentIndex &&
                            approach === "twoPass"
                          ? "bg-purple-200 border-purple-500 scale-110"
                          : idx === currentStepData.targetPosition &&
                            approach === "twoPass"
                          ? "bg-yellow-200 border-yellow-500"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <span className="font-mono font-bold text-sm">
                        {node.isDummy ? "D" : node.value}
                      </span>
                    </div>
                    <div className="text-xs text-center mt-1 text-gray-500">
                      {node.isDummy ? "dummy" : `[${idx - 1}]`}
                    </div>

                    {/* Pointer labels */}
                    {approach === "twoPointer" && (
                      <>
                        {idx === currentStepData.slowIndex + 1 && (
                          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-orange-700">
                            slow
                          </div>
                        )}
                        {idx === currentStepData.fastIndex + 1 && (
                          <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-green-700">
                            fast
                          </div>
                        )}
                      </>
                    )}

                    {/* Arrow to next */}
                    {idx < currentStepData.nodes.length - 1 && (
                      <div className="absolute top-1/2 -right-2 transform -translate-y-1/2">
                        <ArrowRight size={16} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  Operation Complete!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Successfully removed the {n}
                  {n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"} node
                  from end
                </p>
                <p className="text-xs text-green-700">
                  New list: [
                  {currentStepData.nodes.map((n) => n.value).join(", ")}]
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              {approach === "twoPointer"
                ? "Two Pointer Algorithm"
                : "Two Pass Algorithm"}
            </h3>
            <div className="space-y-0.5">
              {(approach === "twoPointer"
                ? twoPointerPseudoCode
                : twoPassPseudoCode
              ).map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="grid grid-cols-2 gap-3">
              <div
                className={`p-2 rounded ${
                  approach === "twoPointer" ? "bg-blue-50" : "bg-gray-50"
                }`}
              >
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                  Two Pointer
                </h4>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium">Time:</span> O(n)
                  </div>
                  <div>
                    <span className="font-medium">Space:</span> O(1)
                  </div>
                  <div className="text-gray-600">One pass</div>
                </div>
              </div>
              <div
                className={`p-2 rounded ${
                  approach === "twoPass" ? "bg-blue-50" : "bg-gray-50"
                }`}
              >
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                  Two Pass
                </h4>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium">Time:</span> O(n)
                  </div>
                  <div>
                    <span className="font-medium">Space:</span> O(1)
                  </div>
                  <div className="text-gray-600">Two passes</div>
                </div>
              </div>
            </div>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">How It Works</h3>
            {approach === "twoPointer" ? (
              <ol className="space-y-1 text-xs text-gray-700">
                <li className="flex">
                  <span className="font-medium mr-1">1.</span>
                  <span>
                    Use dummy node to handle edge case of removing head
                  </span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">2.</span>
                  <span>Move fast pointer n+1 steps ahead</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">3.</span>
                  <span>Move both pointers until fast reaches end</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">4.</span>
                  <span>Slow pointer is now at node before target</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">5.</span>
                  <span>Remove target by updating slow.next</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-1 text-xs text-gray-700">
                <li className="flex">
                  <span className="font-medium mr-1">1.</span>
                  <span>First pass: count total number of nodes</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">2.</span>
                  <span>Calculate position from start: length - n</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">3.</span>
                  <span>Handle edge case if removing first node</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">4.</span>
                  <span>Second pass: navigate to node before target</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">5.</span>
                  <span>Remove target by updating next pointer</span>
                </li>
              </ol>
            )}
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Dummy node eliminates special case for removing head
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Two-pointer maintains n-node gap between pointers</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>When fast reaches end, slow is at correct position</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Both approaches have same complexity, but one pass is elegant
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Finding "nth from end" converts to "length-n from start"
                </span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-300 border-2 border-gray-500 rounded flex items-center justify-center font-bold">
                  D
                </div>
                <span>Dummy node (simplifies edge cases)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-200 border-2 border-orange-500 rounded"></div>
                <span>Slow pointer (two-pointer approach)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-200 border-2 border-green-500 rounded"></div>
                <span>Fast pointer (two-pointer approach)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-200 border-2 border-purple-500 rounded"></div>
                <span>Current position (two-pass approach)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-200 border-2 border-red-500 rounded"></div>
                <span>Node to be removed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveNthNodeVisualizer;
